/**
 * Light / dark / auto theme. "auto" follows the system; the choice is stored on this
 * device and applied as data-theme on <html> (index.html applies it before first paint).
 */
import { useSyncExternalStore } from "react";

export type Theme = "auto" | "light" | "dark";
const KEY = "leanring:theme";
const listeners = new Set<() => void>();

export function getTheme(): Theme {
  try {
    const v = localStorage.getItem(KEY);
    return v === "light" || v === "dark" ? v : "auto";
  } catch {
    return "auto";
  }
}

export function applyTheme(t: Theme): void {
  const root = document.documentElement;
  if (t === "auto") root.removeAttribute("data-theme");
  else root.setAttribute("data-theme", t);
}

export function setTheme(t: Theme): void {
  try {
    if (t === "auto") localStorage.removeItem(KEY);
    else localStorage.setItem(KEY, t);
  } catch {
    /* ignore */
  }
  applyTheme(t);
  listeners.forEach((l) => l());
}

/** Whether the page is currently dark (resolved from the choice and the system). */
export function isDark(): boolean {
  const t = getTheme();
  return t === "dark" || (t === "auto" && window.matchMedia?.("(prefers-color-scheme: dark)").matches);
}

export function useTheme(): Theme {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    getTheme,
    () => "auto" as Theme,
  );
}
